/**
 * Content formatting handlers for TextboxContentFormatter
 */
import { applyFormatToSelection } from "./formattingHandlers";

/**
 * Create color change handler
 */
export const createHandleColorChange = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
  applyFormatToSelection: applyFormatFn,
}) => {
  return (newColor) => {
    const selection = window.getSelection();
    const hasSelection =
      selection && !selection.isCollapsed && selection.rangeCount > 0;

    let didFormat = false;

    if (hasSelection) {
      didFormat = applyFormatFn((span) => {
        span.style.color = newColor;
      });
    } else {
      // No selection, apply color to whole content
      const el = document.getElementById(`content-div-${text?.id}`);
      if (el) {
        const content = el.innerHTML;
        const wrapper = document.createElement("div");
        wrapper.innerHTML = content;

        const applyColorToAllText = (node) => {
          if (node.nodeType === 3) {
            const span = document.createElement("span");
            span.style.color = newColor;
            span.textContent = node.textContent;
            node.parentNode.replaceChild(span, node);
          } else if (node.nodeType === 1) {
            if (!node.style || !node.style.color) {
              node.style.color = newColor;
            }
            Array.from(node.childNodes).forEach((child) => {
              applyColorToAllText(child);
            });
          }
        };

        Array.from(wrapper.childNodes).forEach((node) => {
          applyColorToAllText(node);
        });

        el.innerHTML = wrapper.innerHTML;
        didFormat = true;
      }
    }

    if (didFormat) {
      const el = document.getElementById(`content-div-${text?.id}`);
      if (el) {
        const content = el.innerHTML;
        const plainText = el.innerText || el.textContent || "";
        const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);

        setTexts((prev) =>
          prev.map((t) =>
            t.id === text.id
              ? {
                  ...t,
                  content,
                  plainText,
                  isHtml,
                  contentColor: newColor,
                  _lastUpdated: Date.now(),
                }
              : t
          )
        );

        setSelectedText((prev) => ({
          ...prev,
          content,
          plainText,
          isHtml,
          contentColor: newColor,
          _lastUpdated: Date.now(),
        }));

        saveToHistory();
      }
    }
  };
};

/**
 * Create underline change handler
 */
export const createHandleUnderlineChange = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
  updateFormattedText,
}) => {
  return () => {
    const el = document.getElementById(`content-div-${text?.id}`);
    if (!el) return;

    const selection = window.getSelection();
    const hasSelection =
      selection && !selection.isCollapsed && selection.rangeCount > 0;

    if (hasSelection) {
      const selectedTextContent = selection.toString().trim();
      if (selectedTextContent.length === 0) {
        return;
      }

      const didFormat = applyFormatToSelection((span, selectedEl) => {
        let parent = selectedEl;
        let isUnderlined = false;

        if (selectedEl?.style?.textDecoration === "underline") {
          isUnderlined = true;
        } else {
          while (parent && parent !== el) {
            const style = window.getComputedStyle(parent);
            if (style.textDecorationLine.includes("underline")) {
              isUnderlined = true;
              break;
            }
            parent = parent.parentNode;
          }
        }

        if (isUnderlined) {
          const range = selection.getRangeAt(0);
          const selectedContent = range.cloneContents();
          const tempDiv = document.createElement("div");
          tempDiv.appendChild(selectedContent);

          const removeUnderline = (element) => {
            if (element.style?.textDecoration === "underline") {
              element.style.textDecoration = "none";
            }
            Array.from(element.children).forEach(removeUnderline);
          };

          removeUnderline(tempDiv);
          span.innerHTML = tempDiv.innerHTML;
          span.style.textDecoration = "none";
        } else {
          span.style.textDecoration = "underline";
        }
      });

      if (!didFormat) {
        return;
      }
    } else {
      if (el.innerText.trim().length === 0) {
        return;
      }

      const isUnderlined =
        el.querySelector("[style*='underline']") ||
        window.getComputedStyle(el).textDecorationLine.includes("underline");

      if (isUnderlined) {
        const elements = el.querySelectorAll("*");
        elements.forEach((element) => {
          if (element.style && element.style.textDecoration === "underline") {
            element.style.textDecoration = "none";
          }
        });
      } else {
        const span = document.createElement("span");
        span.style.textDecoration = "underline";
        span.innerHTML = el.innerHTML;
        el.innerHTML = "";
        el.appendChild(span);
      }
    }

    // Sync state
    const updatedHtml = el.innerHTML.trim();
    const updatedPlainText = el.innerText.trim();
    const isHtml = /<\/?[a-z][\s\S]*?>/i.test(updatedHtml);
    const hasUnderline =
      updatedHtml.includes("text-decoration: underline") ||
      updatedHtml.includes('style="text-decoration: underline"') ||
      window.getComputedStyle(el).textDecorationLine.includes("underline");

    updateFormattedText(text.id, "content", () => {
      setTexts((prev) =>
        prev.map((t) =>
          t.id === text.id
            ? {
                ...t,
                content: updatedHtml,
                contentPlainText: updatedPlainText,
                contentIsHtml: isHtml,
                contentUnderline: hasUnderline,
                _lastUpdated: Date.now(),
              }
            : t
        )
      );

      setSelectedText((prev) =>
        prev
          ? {
              ...prev,
              content: updatedHtml,
              contentPlainText: updatedPlainText,
              contentIsHtml: isHtml,
              contentUnderline: hasUnderline,
              _lastUpdated: Date.now(),
            }
          : prev
      );

      saveToHistory();
    });
  };
};

/**
 * Create numbered list handler
 */
export const createHandleNumberedList = ({ text, updateFormattedText }) => {
  return () => {
    const selection = window.getSelection();
    const hasSelection =
      selection && !selection.isCollapsed && selection.rangeCount > 0;

    const el = document.getElementById(`content-div-${text?.id}`);
    if (!el) return;

    const selSave = window.getSelection();
    const savedRange =
      selSave && selSave.rangeCount ? selSave.getRangeAt(0).cloneRange() : null;

    el.focus();
    document.execCommand("insertOrderedList");

    setTimeout(() => {
      if (savedRange) {
        const s = window.getSelection();
        if (s) {
          s.removeAllRanges();
          s.addRange(savedRange);
        }
      }
      updateFormattedText(text.id, "content", () => {});
    }, 0);
  };
};

/**
 * Create bulleted list handler
 */
export const createHandleBulletedList = ({ text, updateFormattedText }) => {
  return () => {
    const selection = window.getSelection();
    const hasSelection =
      selection && !selection.isCollapsed && selection.rangeCount > 0;

    const el = document.getElementById(`content-div-${text?.id}`);
    if (!el) return;

    const selSave = window.getSelection();
    const savedRange =
      selSave && selSave.rangeCount ? selSave.getRangeAt(0).cloneRange() : null;

    el.focus();
    document.execCommand("insertUnorderedList");

    setTimeout(() => {
      if (savedRange) {
        const s = window.getSelection();
        if (s) {
          s.removeAllRanges();
          s.addRange(savedRange);
        }
      }
      updateFormattedText(text?.id, "content", () => {});
    }, 0);
  };
};

/**
 * Create highlight change handler for content (not parts)
 */
export const createHandleContentHighlightChange = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
  updateFormattedText,
}) => {
  return () => {
    const selection = window.getSelection();
    const hasSelection =
      selection && selection.rangeCount > 0 && !selection.isCollapsed;

    const el = document.getElementById(`content-div-${text?.id}`);
    if (!el) return;

    const applyFormatToSelectionLocal = (formatter) => {
      const range = selection.getRangeAt(0);
      const span = document.createElement("span");
      formatter(span);

      span.style.backgroundColor = "yellow";
      span.style.display = "inline-block";
      span.style.padding = "0px";
      span.style.margin = "0px";
      span.style.lineHeight = "1";
      span.style.wordBreak = "break-word";

      span.appendChild(range.extractContents());
      range.insertNode(span);
      return true;
    };

    const updateState = () => {
      const updatedHtml = el.innerHTML.trim();
      const updatedPlainText = el.innerText.trim();
      const isHtml = /<\/?[a-z][\s\S]*?>/i.test(updatedHtml);

      updateFormattedText(text?.id, "content", () => {
        setTexts((prev) =>
          prev.map((t) =>
            t.id === text.id
              ? {
                  ...t,
                  content: updatedHtml,
                  plainText: updatedPlainText,
                  isHtml,
                  contentHighlight: true,
                  _lastUpdated: Date.now(),
                }
              : t
          )
        );
        setSelectedText((prev) => ({
          ...prev,
          content: updatedHtml,
          plainText: updatedPlainText,
          isHtml,
          contentHighlight: true,
          _lastUpdated: Date.now(),
        }));

        saveToHistory();
      });
    };

    if (hasSelection) {
      const didFormat = applyFormatToSelectionLocal((span) => {});
      if (didFormat) updateState();
    } else {
      const hasHighlight = el.querySelector("span[style*='background-color']");
      if (hasHighlight) {
        const spans = el.querySelectorAll("span[style*='background-color']");
        spans.forEach((span) => {
          const parent = span.parentNode;
          while (span.firstChild) parent.insertBefore(span.firstChild, span);
          parent.removeChild(span);
        });
      } else {
        const wrapper = document.createElement("span");
        wrapper.style.backgroundColor = "yellow";
        wrapper.style.display = "inline-block";
        wrapper.style.padding = "0";
        wrapper.style.margin = "0";
        wrapper.style.lineHeight = "1";
        wrapper.style.wordBreak = "break-word";

        wrapper.innerHTML = el.innerHTML;
        el.innerHTML = "";
        el.appendChild(wrapper);
      }
      updateState();
    }
  };
};

/**
 * Create link change handler
 */
export const createHandleLinkChange = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
  updateFormattedText,
}) => {
  return () => {
    const selection = window.getSelection();
    const hasSelection =
      selection && selection.rangeCount > 0 && !selection.isCollapsed;

    const url = prompt("Enter URL", "https://");
    if (!url) return;

    const el = document.getElementById(`content-div-${text?.id}`);
    if (!el) return;

    if (hasSelection) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.textContent = selectedText;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";

      range.deleteContents();
      range.insertNode(anchor);

      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(anchor);
      newRange.collapse(false);
      selection.addRange(newRange);
    } else {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.textContent = el.textContent || el.innerText;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";

      el.innerHTML = "";
      el.appendChild(anchor);

      const range = document.createRange();
      range.selectNodeContents(anchor);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    updateFormattedText(text.id, "content", () => {
      setTexts((prev) =>
        prev.map((t) =>
          t.id === text.id
            ? {
                ...t,
                content: el.innerHTML,
                plainText: el.innerText || el.textContent || "",
                isHtml: true,
                _lastUpdated: Date.now(),
              }
            : t
        )
      );

      setSelectedText((prev) => ({
        ...prev,
        content: el.innerHTML,
        plainText: el.innerText || el.textContent || "",
        isHtml: true,
        _lastUpdated: Date.now(),
      }));

      saveToHistory();
    });
  };
};
